import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, MultiSelectField, normalizeOptions } from './fields';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import {
  EdiDocSetupOptions, DocLine, getEdiDocSetupOptions, listDocLines, saveDocLines,
} from '../../services/edidocsetup.service';

/**
 * Bespoke edit/create drawer for EDI Document Setup (cms.edi_document_setup + an
 * edi_document_setup_lines Document-Configuration subtable).
 *
 * Mirrors V1 editedi_document_setup: EDI Handle / Automated? / CMS Code selects,
 * Bill To(s) / Customer(s) / Broker(s) — stored as comma-delimited recno lists but
 * edited here as name multiselects — then an inline Document Configurations editor
 * (Document Type select from the CMS doc-type catalogue + open-entry Translation
 * Code). Save rules: at least one of Bill To / Customer / CMS Code; each line needs
 * a Document Type + Translation Code. Removed lines are soft-deleted.
 */
const TABLE = 'edi-document-setup';

const s = (v: any): string => (v == null ? '' : String(v));
const splitList = (v: any): string[] => s(v).split(',').map((x) => x.trim()).filter(Boolean);
const YN_OPTS = [{ value: 'N', label: 'No' }, { value: 'Y', label: 'Yes' }];

// Enriched FK-picker label: "Name · City, ST · #recno". The recno disambiguates
// duplicate names (and MultiSelect searches label OR value, so typing the recno or
// city filters too) — matching the enriched selects elsewhere.
const optLabel = (name: any, city: any, state: any, recno: any): string => {
  const loc = [titleCase(s(city)), s(state)].filter(Boolean).join(', ');
  return [titleCase(s(name)), loc, `#${recno}`].filter(Boolean).join('  ·  ');
};

const blankLine = (): DocLine => ({ recno: 0, documenttype: '', translationcode: '' });

const EdiDocumentSetupEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [opts, setOpts] = useState<EdiDocSetupOptions | null>(null);
  const [form, setForm] = useState<TablemaintRow>({ edihandle: '', cmscode: '', automated: 'N' });
  const [billtos, setBilltos] = useState<string[]>([]);
  const [customers, setCustomers] = useState<string[]>([]);
  const [brokers, setBrokers] = useState<string[]>([]);
  const [formDeleted, setFormDeleted] = useState(false);
  const [lines, setLines] = useState<DocLine[]>([]);
  const [deletes, setDeletes] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getEdiDocSetupOptions().then(setOpts).catch(() => {});
    if (!isNew) {
      tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
      listDocLines(recno).then(setLines).catch(() => {});
    }
  }, [isNew, recno]);

  useEffect(() => {
    setForm({ edihandle: s(record.edihandle), cmscode: s(record.cmscode), automated: s(record.automated) || 'N' });
    setBilltos(splitList(record.billtolist));
    setCustomers(splitList(record.customerlist));
    setBrokers(splitList(record.brokerlist));
    setFormDeleted(s(record.deleted) === 'Y');
  }, [record]);

  useEffect(() => { if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const setLine = (idx: number, key: keyof DocLine, value: string) =>
    setLines((prev) => prev.map((ln, i) => (i === idx ? { ...ln, [key]: value } : ln)));
  const addLine = () => setLines((prev) => [...prev, blankLine()]);
  const removeLine = (idx: number) => setLines((prev) => {
    const ln = prev[idx];
    if (ln.recno) setDeletes((d) => [...d, ln.recno]);
    return prev.filter((_, i) => i !== idx);
  });

  const edihandleOpts = useMemo(() => normalizeOptions(opts?.edihandles ?? []), [opts]);
  const cmscodeOpts = useMemo(() => normalizeOptions(opts?.cmscodes ?? []), [opts]);
  const doctypeOpts = useMemo(() => normalizeOptions(opts?.documenttypes ?? []), [opts]);
  const billtoOpts = useMemo(() => (opts?.billtos ?? []).map((b) => ({ value: String(b.recno), label: optLabel(b.cname, b.city, b.state, b.recno) })), [opts]);
  const customerOpts = useMemo(() => (opts?.customers ?? []).map((c) => ({ value: String(c.recno), label: optLabel(c.custname, c.city, c.state, c.recno) })), [opts]);
  const brokerOpts = useMemo(() => (opts?.brokers ?? []).map((b) => ({ value: String(b.recno), label: optLabel(b.name, b.city, b.state, b.recno) })), [opts]);

  const errors = useMemo(() => {
    const errs: string[] = [];
    if (!s(form.edihandle).trim()) errs.push('EDI Handle is required.');
    if (billtos.length === 0 && customers.length === 0 && !s(form.cmscode).trim()) {
      errs.push('Enter at least one Bill To, Customer, or CMS Code.');
    }
    lines.forEach((ln) => {
      const dt = s(ln.documenttype).trim();
      const tc = s(ln.translationcode).trim();
      if (!dt && !tc) return;
      if (!dt) errs.push('Document Type is required on each configuration line.');
      else if (!tc) errs.push(`Translation Code is required for ${dt}.`);
    });
    return errs;
  }, [billtos, customers, form, lines]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    const master = {
      edihandle: s(form.edihandle), cmscode: s(form.cmscode), automated: s(form.automated) || 'N',
      billtolist: billtos.join(','), customerlist: customers.join(','), brokerlist: brokers.join(','),
    };
    const outLines = lines.filter((l) => s(l.documenttype).trim() || s(l.translationcode).trim());
    try {
      let mainRecno = recno;
      if (isNew) {
        const res = await tablemaintService.create(TABLE, master);
        mainRecno = res.recno;
      } else {
        const patch: TablemaintRow = {};
        (Object.keys(master) as (keyof typeof master)[]).forEach((k) => {
          const cur = k === 'billtolist' ? splitList(record.billtolist).join(',')
            : k === 'customerlist' ? splitList(record.customerlist).join(',')
            : k === 'brokerlist' ? splitList(record.brokerlist).join(',')
            : s(record[k]);
          if (master[k] !== cur) patch[k] = master[k];
        });
        if (formDeleted !== (s(record.deleted) === 'Y')) patch.deleted = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length) await tablemaintService.update(TABLE, recno, patch);
      }
      await saveDocLines(mainRecno, outLines, isNew ? [] : deletes);
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save EDI document setup.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, isNew, form, billtos, customers, brokers, record, formDeleted, lines, deletes, recno, onClose, onSaved]);

  const cellCls = 'w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white disabled:opacity-50';
  // Document-type cell options, injecting the line's current value if it's a legacy
  // one no longer in the catalogue (V1 smod_forcevalueinarray) so it isn't lost.
  const doctypeCellOpts = (current: string) => {
    const cur = s(current).trim();
    return cur && !doctypeOpts.some((o) => o.value === cur)
      ? [{ value: cur, label: `${cur} (legacy)` }, ...doctypeOpts]
      : doctypeOpts;
  };

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Setup' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add EDI Document Setup' : `Edit EDI Document Setup (#${recno})`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="60rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
          <legend className="px-2 text-sm font-semibold text-label">
            Setup <span className="text-xs font-normal text-gray-400">(at least one of Bill To / Customer / CMS Code)</span>
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SelectField label="EDI Handle" required value={s(form.edihandle)} onChange={(v) => setField('edihandle', v)}
              options={edihandleOpts} disabled={isSaving} />
            <SelectField label="Automated?" value={s(form.automated)} onChange={(v) => setField('automated', v)}
              options={YN_OPTS} clearable={false} disabled={isSaving} />
            <SelectField label="CMS Code" value={s(form.cmscode)} onChange={(v) => setField('cmscode', v)}
              options={cmscodeOpts} disabled={isSaving} />
          </div>
          <div className="grid grid-cols-1 gap-4">
            <MultiSelectField label="Bill To(s)" value={billtos} onChange={setBilltos} options={billtoOpts}
              disabled={isSaving} loading={!opts} placeholder="Add bill-tos…" />
            <MultiSelectField label="Customer(s)" value={customers} onChange={setCustomers} options={customerOpts}
              disabled={isSaving} loading={!opts} placeholder="Add customers…" />
            <MultiSelectField label="Broker(s)" value={brokers} onChange={setBrokers} options={brokerOpts}
              disabled={isSaving} loading={!opts} placeholder="Add brokers…" />
          </div>
        </fieldset>

        {/* Document configurations. */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Document Configurations</legend>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="text-left text-muted">
                  <th className="py-1 pr-2 font-medium">Document Type</th>
                  <th className="py-1 px-2 font-medium">Translation Code</th>
                  <th className="py-1 pl-2" />
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr><td colSpan={3} className="py-2 text-gray-400 dark:text-gray-500">No document configurations yet.</td></tr>
                ) : lines.map((ln, idx) => (
                  <tr key={idx} className="border-t border-gray-100 dark:border-gray-700/60 align-top">
                    <td className="py-1.5 pr-2 w-[45%]">
                      <select value={s(ln.documenttype)} disabled={isSaving} className={cellCls}
                        onChange={(e) => setLine(idx, 'documenttype', e.target.value)}>
                        {doctypeCellOpts(s(ln.documenttype)).map((o) => <option key={o.value} value={o.value}>{o.label || '—'}</option>)}
                      </select>
                    </td>
                    <td className="py-1.5 px-2 w-[45%]">
                      <input type="text" value={s(ln.translationcode)} disabled={isSaving} maxLength={50}
                        onChange={(e) => setLine(idx, 'translationcode', e.target.value)} className={cellCls} />
                    </td>
                    <td className="py-1.5 pl-2 text-right">
                      <button type="button" onClick={() => removeLine(idx)} disabled={isSaving}
                        className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" title="Remove line">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={addLine} disabled={isSaving}
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
            <PlusIcon className="w-4 h-4" /> Add configuration
          </button>
        </fieldset>

        {!isNew && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <label className="flex items-start gap-3 cursor-pointer select-none py-1">
              <input type="checkbox" checked={formDeleted} disabled={isSaving}
                onChange={(e) => setFormDeleted(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
              <span className="text-sm font-medium text-label">Deleted</span>
            </label>
            <p className="mt-1 ml-7 text-xs text-muted">
              Soft-deletes this setup — hidden from filters but preserved in history. Uncheck and save to restore.
            </p>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default EdiDocumentSetupEditDrawer;
